import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, where, runTransaction, setDoc } from 'firebase/firestore';
import { db } from '../pages/app/protected/firebase';
import { useAuth } from './AuthContext';

export const ContactContext = createContext();

export const ContactProvider = ({ children }) => {
  const { user, companyId } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch contacts on mount and when user changes
  useEffect(() => {
    if (user?.uid || companyId) {
      const unsubscribe = fetchContacts();
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [user?.uid, companyId]);

  // Get the next auto-incremented contact ID
  const getNextContactId = async () => {
    if (!user?.uid || !companyId) {
      throw new Error('User not authenticated');
    }

    const counterRef = doc(db, 'companies', companyId, 'companySettings', 'contactCounter');
    
    try {
      // Start a transaction to ensure atomic increment
      const result = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        
        // Initialize counter if it doesn't exist
        let newCount = 1;
        if (counterDoc.exists()) {
          newCount = (counterDoc.data().lastId || 0) + 1;
        }
        
        // Update the counter in companySettings
        transaction.set(counterRef, { 
          lastId: newCount,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        
        return newCount;
      });
      
      // Format the ID as a simple number (e.g., 1, 2, 3, ...)
      return result;
    } catch (error) {
      console.error('Error getting next contact ID:', error);
      // Fallback to timestamp if counter fails
      console.log('Falling back to timestamp-based ID');
      return Date.now();
    }
  };

  // Get the contacts collection reference
  const getContactsCollection = () => {
    if (!user?.uid || !companyId) {
      throw new Error('User not authenticated');
    }
    return collection(db, 'companies', companyId, 'contacts');
  };

  // Add a new contact with auto-incremented ID
  const addContact = async (contact) => {
    console.log('addContact called with:', contact);
    
    try {
      const contactId = await getNextContactId();
      console.log('Generated contact ID:', contactId);
      
      const contactsCollection = getContactsCollection();
      
      const contactData = {
        ...contact,
        id: contactId, // Set the id field to match the document ID
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      console.log('Contact data to be saved:', contactData);
      
      // Create a document reference with the contactId as the document ID
      const docRef = doc(contactsCollection, String(contactId));
      console.log('Document reference created:', docRef.path);
      
      await setDoc(docRef, contactData);
      console.log('Contact saved successfully');
      
      return { id: contactId, ...contactData };
    } catch (err) {
      console.error('Error in addContact:', err);
      setError('Failed to add contact: ' + (err.message || 'Unknown error'));
      throw err;
    }
  };

  // Update an existing contact
  const updateContact = async (id, updates) => {
    console.log('updateContact called with ID:', id, 'and updates:', updates);
    
    if (!id) {
      const errorMsg = 'Cannot update contact: No ID provided';
      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      const contactsCollection = getContactsCollection();
      const docRef = doc(contactsCollection, String(id)); // Ensure ID is a string for Firestore
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString(),
      });
      
      console.log('Contact updated successfully');
      return true;
    } catch (err) {
      console.error('Error in updateContact:', err);
      setError('Failed to update contact: ' + (err.message || 'Unknown error'));
      throw new Error('Failed to update contact: ' + (err.message || 'Unknown error'));
    }
  };

  // Delete a contact
  const deleteContact = async (id) => {
    console.log('deleteContact called with ID:', id);
    
    if (!id) {
      const errorMsg = 'Cannot delete contact: No ID provided';
      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      const contactsCollection = getContactsCollection();
      const docRef = doc(contactsCollection, String(id)); // Ensure ID is a string for Firestore
      
      await deleteDoc(docRef);
      console.log('Contact deleted successfully');
      return true;
    } catch (err) {
      console.error('Error in deleteContact:', err);
      setError('Failed to delete contact: ' + (err.message || 'Unknown error'));
      throw new Error('Failed to delete contact: ' + (err.message || 'Unknown error'));
    }
  };

  // Fetch contacts with optional type filter
  const fetchContacts = (type = null) => {
    setLoading(true);
    
    try {
      const contactsCollection = getContactsCollection();
      let q = query(contactsCollection);
      
      if (type) {
        q = query(contactsCollection, where('type', '==', type));
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const contactsData = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            contactsData.push({ 
              // Use either data.id or doc.id, with doc.id as fallback
              id: data.id || doc.id,
              ...data
            });
          });
          setContacts(contactsData);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching contacts:', error);
          setError('Failed to fetch contacts');
          setLoading(false);
        }
      );

      return unsubscribe;
    } catch (err) {
      console.error('Error in fetchContacts:', err);
      setError('Failed to initialize contacts');
      setLoading(false);
      return () => {}; // Return empty cleanup function
    }
  };

  return (
    <ContactContext.Provider
      value={{
        contacts,
        loading,
        error,
        addContact,
        updateContact,
        deleteContact,
        fetchContacts,
      }}
    >
      {children}
    </ContactContext.Provider>
  );
};

export const useContacts = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContacts must be used within a ContactProvider');
  }
  return context;
};
